import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    Box,
    Card,
    Radio,
} from '@mui/material';
import { FontVariantToken } from '@uipath/apollo-core';
import React from 'react';

interface UiRadioCardProps {
    id: string;
    title: string;
    description: string;
    icon?: React.ReactNode;
    isSelected: boolean;
    onCardClick: (value: string) => void;
    onRadioChange?: (value: string) => void;
    dataCyPrefix?: string;
    value: string;
    disabled?: boolean;
}

export const UiRadioCard: React.FC<UiRadioCardProps> = ({
    id,
    title,
    description,
    icon,
    isSelected,
    onCardClick,
    onRadioChange,
    dataCyPrefix = 'ui-radio-card',
    value,
    disabled = false,
}) => {
    const handleCardClick = () => {
        if (!disabled) {
            onCardClick(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onCardClick(value);
        }
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onRadioChange) {
            onRadioChange(e.target.value);
        }
    };

    return (
        <Card
            elevation={0}
            sx={{
                width: 'inherit',
                padding: '16px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                borderStyle: 'solid',
                borderColor: isSelected ? 'var(--color-primary)' : 'var(--color-border-de-emp)',
                borderWidth: '1px',
                borderRadius: '8px',
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                '&:hover': disabled ? {} : { backgroundColor: 'var(--color-background-selected)' },
                backgroundColor: isSelected ? 'var(--color-background-selected)' : 'var(--color-background-default)',
            }}
            onClick={handleCardClick}
            data-cy={`${dataCyPrefix}-${id}`}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onKeyDown={handleKeyDown}
        >
            <UiStack
                direction="row"
                align="start"
                justify="between"
                gap={SpacingToken.M}
            >
                <UiStack
                    direction="row"
                    gap={SpacingToken.M}
                    align="start"
                >
                    {icon && (
                        <UiStack
                            align="start"
                            data-cy={`${dataCyPrefix}-icon-${id}`}
                            style={{ color: isSelected ? 'var(--color-primary)' : 'var(--color-icon-default)' }}
                        >
                            {icon}
                        </UiStack>
                    )}

                    <UiStack
                        direction="column"
                        gap={SpacingToken.XS}
                    >
                        <UiText
                            variant={FontVariantToken.fontSizeMBold}
                            data-cy={`${dataCyPrefix}-title-${id}`}
                        >
                            {title}
                        </UiText>
                        <UiText
                            variant={FontVariantToken.fontSizeM}
                            color="var(--color-foreground-de-emp)"
                            data-cy={`${dataCyPrefix}-description-${id}`}
                        >
                            {description}
                        </UiText>
                    </UiStack>
                </UiStack>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        paddingTop: '2px',
                    }}
                >
                    <Radio
                        value={value}
                        checked={isSelected}
                        onChange={handleRadioChange}
                        disabled={disabled}
                        data-cy={`${dataCyPrefix}-radio-${id}`}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Box>
            </UiStack>
        </Card>
    );
};
